// Extrato de conta, espelhando o app:
//  - só lançamento CONFIRMADO move saldo (lib/accountProjection.ts + getDailyBalanceTimeline);
//    receita +, despesa − (pagamento de fatura é despesa na conta), transferência sai de uma e
//    entra na outra, ajuste soma o valor com sinal;
//  - lançamento ignorado sai da análise mas mantém o dinheiro (CONTEXT.md);
//  - pendente e projetado aparecem à parte e não movem o saldo;
//  - a posição de HOJE é o currentBalance que o app mostra; o passado se reconstrói andando
//    pra trás a partir dele, então o último dia bate sempre com o tile.
import type { AccountRow, ReservaRow, TransactionRow } from './types';
import { addMonths, cap, dayLong } from './format';
import { daysUntil } from './recurring';
import { isInvoiceSettlement } from './model';

/** Efeito do lançamento no saldo da conta (0 se não a toca). Port de applyAccountBalanceImpact. */
export function impactOn(t: TransactionRow, accountId: string): number {
  if (t.type === 'income') return t.accountId === accountId ? t.amount : 0;
  if (t.type === 'expense') return t.accountId === accountId ? -t.amount : 0;
  if (t.type === 'transfer') return (t.accountId === accountId ? -t.amount : 0) + (t.transferDestinationAccountId === accountId ? t.amount : 0);
  if (t.type === 'adjustment') return t.accountId === accountId ? t.amount : 0;
  return 0;
}

export function movesBalance(t: TransactionRow): boolean {
  return !!t.isConfirmed && !t.isProjected;
}

export function touches(t: TransactionRow, accountId: string): boolean {
  return t.accountId === accountId || t.transferDestinationAccountId === accountId;
}

/** Lançamentos que passam pela conta, na ordem recebida (o model já vem por data desc). */
export function accountRows(transactions: TransactionRow[], accountId: string): TransactionRow[] {
  return transactions.filter(t => touches(t, accountId));
}

export function dayBefore(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d - 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/** Saldo da conta no FIM do dia: o de hoje menos tudo que caiu depois. Só confirmadas contam. */
export function balanceAt(account: AccountRow, all: TransactionRow[], date: string): number {
  let after = 0;
  for (const t of all) if (t.date > date && movesBalance(t)) after += impactOn(t, account.id);
  return Math.round((account.currentBalance - after) * 100) / 100;
}

export interface MonthPoint {
  month: string;
  /** saldo no fim do mês */
  balance: number;
  /** o que entrou menos o que saiu no mês */
  net: number;
}

/** Saldo no fim de cada um dos últimos `count` meses até `endMonth`, em ordem crescente. */
export function balanceByMonth(account: AccountRow, all: TransactionRow[], endMonth: string, count = 12): MonthPoint[] {
  const nets = new Map<string, number>();
  for (const t of all) {
    if (!movesBalance(t)) continue;
    const v = impactOn(t, account.id);
    if (v) nets.set(t.date.slice(0, 7), (nets.get(t.date.slice(0, 7)) ?? 0) + v);
  }
  const lastDay = `${endMonth}-31`;
  let balance = balanceAt(account, all, lastDay);
  const out: MonthPoint[] = [];
  let month = endMonth;
  for (let i = 0; i < count; i++) {
    const net = Math.round((nets.get(month) ?? 0) * 100) / 100;
    out.unshift({ month, balance, net });
    balance = Math.round((balance - net) * 100) / 100;
    month = addMonths(month, -1);
  }
  return out;
}

export interface Flow {
  /** dinheiro que entrou na conta (receitas, transferências recebidas, ajustes positivos) */
  in: number;
  /** dinheiro que saiu (despesas, faturas pagas, transferências enviadas, ajustes negativos) */
  out: number;
  net: number;
  count: number;
}

export function flowOf(rows: TransactionRow[], accountId: string): Flow {
  const f: Flow = { in: 0, out: 0, net: 0, count: 0 };
  for (const t of rows) {
    if (!movesBalance(t)) continue;
    const v = impactOn(t, accountId);
    if (!v) continue;
    f.count += 1;
    if (v > 0) f.in += v; else f.out += -v;
  }
  f.net = Math.round((f.in - f.out) * 100) / 100;
  return f;
}

export type Kind = 'in' | 'out' | 'transfer' | 'invoice' | 'adjustment';
export const KIND_LABEL: Record<Kind, string> = {
  in: 'Entradas',
  out: 'Saídas',
  transfer: 'Transferências',
  invoice: 'Pagamentos de fatura',
  adjustment: 'Ajustes',
};

export function kindOf(t: TransactionRow): Kind {
  if (t.type === 'transfer') return 'transfer';
  if (t.type === 'adjustment') return 'adjustment';
  if (isInvoiceSettlement(t)) return 'invoice';
  return t.type === 'income' || (t.type === 'expense' && t.amount < 0 && t.adjustmentType == null) ? 'in' : 'out';
}

export interface DayGroup {
  date: string;
  rows: TransactionRow[];
  /** entrou menos saiu no dia, só do que move saldo */
  net: number;
  /** saldo no fim do dia (só faz sentido sem filtro) */
  balance: number;
}

export interface Statement {
  /** pendentes e projetados do período: não movem o saldo */
  pending: TransactionRow[];
  /** dias em ordem decrescente */
  days: DayGroup[];
}

/** Agrupa por dia (desc) e anda pra trás a partir do saldo no fim do período: o saldo de cada
 * dia é o do dia seguinte menos o que aquele dia moveu. */
export function statementOf(rows: TransactionRow[], accountId: string, endBalance: number): Statement {
  const pending = rows.filter(t => !movesBalance(t));
  const byDay = new Map<string, TransactionRow[]>();
  for (const t of rows) {
    if (!movesBalance(t)) continue;
    const list = byDay.get(t.date);
    if (list) list.push(t); else byDay.set(t.date, [t]);
  }
  const dates = [...byDay.keys()].sort((a, b) => b.localeCompare(a));
  const days: DayGroup[] = [];
  let balance = endBalance;
  for (const date of dates) {
    const list = byDay.get(date)!;
    const net = Math.round(list.reduce((sum, t) => sum + impactOn(t, accountId), 0) * 100) / 100;
    days.push({ date, rows: list, net, balance });
    balance = Math.round((balance - net) * 100) / 100;
  }
  return { pending, days };
}

export function reservedOf(reservas: ReservaRow[], accountId: string): number {
  return reservas.filter(r => r.accountId === accountId).reduce((sum, r) => sum + r.currentAmount, 0);
}

/** "Hoje" · "Ontem" · "Quinta, 4 de setembro" · "Quinta, 4 de setembro de 2025". */
export function dayLabel(date: string, today: string): string {
  const n = daysUntil(date, today);
  if (n === 0) return 'Hoje';
  if (n === -1) return 'Ontem';
  const [dm, wd] = dayLong(date).split(', ');
  return `${cap(wd)}, ${dm}${date.slice(0, 4) !== today.slice(0, 4) ? ` de ${date.slice(0, 4)}` : ''}`;
}
